// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Dependency-free Code 128-B barcode + QR code as inline SVG, for the shipping
 * label print view. No npm deps (keeps the print view self-contained and the
 * bundle lean). Both encode the shipment's tracking/order reference so the
 * Roadpas ecMobile scanner can read it.
 *
 * NOTE: the exact symbology Roadpas expects is to be confirmed with them; the
 * label UI surfaces that caveat. Code 128-B + a QR of the same value is the
 * sensible default and is what virtually all courier scanners accept.
 */

// ── Code 128-B ──────────────────────────────────────────────────────────────
// Bar patterns for values 0..106 (each is 6 digits: bar/space widths).
const CODE128_PATTERNS = [
  '212222','222122','222221','121223','121322','131222','122213','122312','132212','221213',
  '221312','231212','112232','122132','122231','113222','123122','123221','223211','221132',
  '221231','213212','223112','312131','311222','321122','321221','312212','322112','322211',
  '212123','212321','232121','111323','131123','131321','112313','132113','132311','211313',
  '231113','231311','112133','112331','132131','113123','113321','133121','313121','211331',
  '231131','213113','213311','213131','311123','311321','331121','312113','312311','332111',
  '314111','221411','431111','111224','111422','121124','121421','141122','141221','112214',
  '112412','122114','122411','142112','142211','241211','221114','413111','241112','134111',
  '111242','121142','121241','114212','124112','124211','411212','421112','421211','212141',
  '214121','412121','111143','111341','131141','114113','114311','411113','411311','113141',
  '114131','311141','411131','211412','211214','211232','2331112',
]
const START_B = 104
const STOP = 106

/** Returns an SVG string of a Code 128-B barcode for `value`. */
export function code128Svg(value: string, opts?: { height?: number; moduleWidth?: number }): string {
  const height = opts?.height ?? 60
  const mw = opts?.moduleWidth ?? 1.6
  // Build code values: START_B, data (ASCII - 32), checksum, STOP.
  const codes: number[] = [START_B]
  let sum = START_B
  for (let i = 0; i < value.length; i++) {
    const v = value.charCodeAt(i) - 32
    codes.push(v)
    sum += v * (i + 1)
  }
  codes.push(sum % 103)
  codes.push(STOP)

  // Expand to bar widths. A Code 128 symbol needs a QUIET ZONE (>=10 modules of
  // white) on each side or scanners fail to lock on — especially at larger module
  // widths / full-width labels. Offset the bars by the quiet zone and paint a white
  // background behind the whole symbol so it scans on any surface.
  const quiet = 10 * mw
  let x = quiet
  const rects: string[] = []
  for (const c of codes) {
    const pat = CODE128_PATTERNS[c]
    for (let i = 0; i < pat.length; i++) {
      const w = parseInt(pat[i], 10) * mw
      if (i % 2 === 0) {  // even index = bar
        rects.push(`<rect x="${x.toFixed(2)}" y="0" width="${w.toFixed(2)}" height="${height}" />`)
      }
      x += w
    }
  }
  const width = x + quiet
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width.toFixed(2)}" height="${height}" viewBox="0 0 ${width.toFixed(2)} ${height}"><rect x="0" y="0" width="${width.toFixed(2)}" height="${height}" fill="#fff" /><g fill="#000">${rects.join('')}</g></svg>`
}

// ── QR (compact, byte mode, automatic version) ────────────────────────────────
// Minimal QR encoder adapted from the public-domain "qrcode-generator" logic,
// trimmed to byte mode + the few versions a short tracking string needs.
// For label use the payload is short (< 30 chars), so low versions suffice.

// Galois field tables for Reed-Solomon.
const EXP: number[] = new Array(256)
const LOG: number[] = new Array(256)
;(function initGF() {
  let x = 1
  for (let i = 0; i < 255; i++) { EXP[i] = x; LOG[x] = i; x <<= 1; if (x & 0x100) x ^= 0x11d }
  for (let i = 255; i < 256; i++) EXP[i] = EXP[i - 255]
})()
function gfMul(a: number, b: number) { return a === 0 || b === 0 ? 0 : EXP[(LOG[a] + LOG[b]) % 255] }

function rsGenPoly(n: number): number[] {
  let poly = [1]
  for (let i = 0; i < n; i++) {
    const next = new Array(poly.length + 1).fill(0)
    for (let j = 0; j < poly.length; j++) {
      next[j] ^= poly[j]
      next[j + 1] ^= gfMul(poly[j], EXP[i])
    }
    poly = next
  }
  return poly
}
function rsEncode(data: number[], ecLen: number): number[] {
  const gen = rsGenPoly(ecLen)
  const res = new Array(ecLen).fill(0)
  for (const d of data) {
    const factor = d ^ res[0]
    res.shift(); res.push(0)
    for (let i = 0; i < gen.length - 1; i++) res[i] ^= gfMul(gen[i + 1], factor)
  }
  return res
}

// Version params we support (byte-mode, EC level M): [version, totalCodewords, ecPerBlock, dataCodewords]
const QR_VERSIONS = [
  { v: 1, size: 21, ec: 10, data: 16 },
  { v: 2, size: 25, ec: 16, data: 28 },
  { v: 3, size: 29, ec: 26, data: 44 },
  { v: 4, size: 33, ec: 18, data: 64 },
]

/** Returns an SVG string of a QR code for `value`. Falls back gracefully. */
export function qrSvg(value: string, opts?: { size?: number }): string {
  const px = opts?.size ?? 120
  try {
    const bytes = new TextEncoder().encode(value)
    const ver = QR_VERSIONS.find(v => bytes.length + 3 <= v.data) ?? QR_VERSIONS[QR_VERSIONS.length - 1]

    // Build bit stream: mode (0100) + length (8 bits) + data + terminator, pad.
    const bits: number[] = []
    const push = (val: number, len: number) => { for (let i = len - 1; i >= 0; i--) bits.push((val >> i) & 1) }
    push(0b0100, 4)
    push(bytes.length, 8)
    for (const b of bytes) push(b, 8)
    push(0, 4)
    while (bits.length % 8 !== 0) bits.push(0)
    const dataCodewords: number[] = []
    for (let i = 0; i < bits.length; i += 8) {
      let b = 0; for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j]; dataCodewords.push(b)
    }
    const pad = [0xec, 0x11]
    let pi = 0
    while (dataCodewords.length < ver.data) dataCodewords.push(pad[pi++ % 2])

    const ec = rsEncode(dataCodewords, ver.ec)
    const all = dataCodewords.concat(ec)

    // Render modules (simplified: no masking optimization — fixed mask 0).
    const n = ver.size
    const mod: (boolean | null)[][] = Array.from({ length: n }, () => new Array(n).fill(null))

    const placeFinder = (r: number, c: number) => {
      for (let dr = -1; dr <= 7; dr++) for (let dc = -1; dc <= 7; dc++) {
        const rr = r + dr, cc = c + dc
        if (rr < 0 || rr >= n || cc < 0 || cc >= n) continue
        const inRing = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6 &&
          (dr === 0 || dr === 6 || dc === 0 || dc === 6 || (dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4))
        mod[rr][cc] = inRing
      }
    }
    placeFinder(0, 0); placeFinder(0, n - 7); placeFinder(n - 7, 0)
    // timing patterns
    for (let i = 8; i < n - 8; i++) { if (mod[6][i] === null) mod[6][i] = i % 2 === 0; if (mod[i][6] === null) mod[i][6] = i % 2 === 0 }
    mod[n - 8][8] = true // dark module

    // data placement (zig-zag), with fixed mask 0: (r+c)%2==0 inverts
    let bitIdx = 0
    const dataBits: number[] = []
    for (const cw of all) for (let i = 7; i >= 0; i--) dataBits.push((cw >> i) & 1)
    let upward = true
    for (let col = n - 1; col > 0; col -= 2) {
      if (col === 6) col-- // skip timing col
      const rows = upward ? range(n - 1, -1, -1) : range(0, n, 1)
      for (const row of rows) {
        for (let c = 0; c < 2; c++) {
          const cc = col - c
          if (mod[row][cc] !== null) continue
          let dark = bitIdx < dataBits.length ? dataBits[bitIdx++] === 1 : false
          if ((row + cc) % 2 === 0) dark = !dark // mask 0
          mod[row][cc] = dark
        }
      }
      upward = !upward
    }

    const scale = px / n
    const rects: string[] = []
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
      if (mod[r][c]) rects.push(`<rect x="${(c * scale).toFixed(2)}" y="${(r * scale).toFixed(2)}" width="${scale.toFixed(2)}" height="${scale.toFixed(2)}" />`)
    }
    void bitIdx
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 ${px} ${px}" fill="#000"><rect width="${px}" height="${px}" fill="#fff"/>${rects.join('')}</svg>`
  } catch {
    // Fallback: a simple box with the value, so the label still prints.
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}"><rect width="${px}" height="${px}" fill="#fff" stroke="#000"/><text x="50%" y="50%" font-size="8" text-anchor="middle">QR</text></svg>`
  }
}

function range(start: number, end: number, step: number): number[] {
  const out: number[] = []
  if (step > 0) for (let i = start; i < end; i += step) out.push(i)
  else for (let i = start; i > end; i += step) out.push(i)
  return out
}
